import React, { useState } from 'react';
import { CircleCheckIcon, StarIcon, ChevronDownIcon, ChevronUpIcon, BadgeCheckIcon } from 'lucide-react';
import { ImagePlaceholder } from './ImagePlaceholder';
import { PackSelector } from './PackSelector';
import { SellerCard } from './SellerCard';
import { motion, AnimatePresence } from 'framer-motion';
const galleryImages: {
  label: string;
  src?: string;
}[] = [{
  label: 'Pote ESBELTA® 60 gomitas',
  src: 'https://cdn.magicpatterns.com/uploads/suhcPXB4eKx8m116DtCqhf/WhatsApp_Image_2026-06-15_at_11.29.08_AM.jpg'
}, {
  label: 'Sachet ESBELTA® Fórmula Ancestral',
  src: 'https://cdn.magicpatterns.com/uploads/5jY7zkU2rx49nMRtrt8YxG/WhatsApp_Image_2026-06-15_at_11.28.54_AM.jpg'
}, {
  label: 'Imagen 3: Gomita individual close-up textura',
  src: 'https://cdn.magicpatterns.com/uploads/4Xaot7PaRRvTJXofaAJQi7/ded25bef-a02d-4ae1-b48e-b2871057971c.png'
}, {
  label: 'Imagen 4: Mujer madura 40+ sosteniendo el pote, sonriendo naturalmente',
  src: 'https://cdn.magicpatterns.com/uploads/uYjbMA4Vf5ZtRYCnW6jDT9/ceb15e1b-7db8-4d8a-b1ed-b07e835da146.png'
}, {
  label: 'Imagen 5: Tabla de ingredientes visual',
  src: 'https://cdn.magicpatterns.com/uploads/ntcToeb4Y3Cwnh4fww6fqU/6f137073-3d81-4fd2-b140-b230b2fb98fc.png'
}, {
  label: 'Imagen 6: Antes/después sutil (silueta ropa ajustada)',
  src: 'https://cdn.magicpatterns.com/uploads/2QMaGPcxDgKVXQrN2YYQwB/68726e6b-e09e-4e07-9a86-8c2ff2b031a6.png'
}];
const faqs = [{
  q: '¿Qué es ESBELTA®?',
  a: 'ESBELTA® es una gomita funcional con Fórmula Ancestral diseñada para mujeres maduras. Combina 8 activos naturales que trabajan en conjunto para reducir el cortisol elevado, mejorar la sensibilidad a la insulina, apoyar el equilibrio hormonal y acelerar el metabolismo.'
}, {
  q: 'Ingredientes',
  a: 'Inositol, Vitamina B12, Zinc, Magnesio, Vitamina D3, Berberina HCl, Ashwagandha, Cromo.'
}, {
  q: '¿Cómo se toman?',
  a: '2 gomitas por día, de preferencia por la mañana con agua. Se pueden tomar con o sin comida.'
}, {
  q: 'Envíos',
  a: 'Envíos a todo el país. Los pedidos pueden demorar entre 5 y 8 días hábiles.'
}];
export function Hero() {
  const [activeImage, setActiveImage] = useState(0);
  const [selectedPack, setSelectedPack] = useState('pack-4m');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return <section id="ofertas" className="max-w-6xl mx-auto px-4 py-8 scroll-mt-24" data-id="element-116">
      <div className="flex flex-col lg:flex-row gap-10" data-id="element-117">
        {/* Left: Gallery (Stacked on mobile, side on desktop) */}
        <div className="w-full lg:w-1/2 flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start" data-id="element-118">
          <div className="aspect-square w-full rounded-2xl overflow-hidden bg-white" data-id="element-119">
            {galleryImages[activeImage].src ? <img src={galleryImages[activeImage].src} alt={galleryImages[activeImage].label} className="w-full h-full object-contain" data-id="element-120" /> : <ImagePlaceholder label={galleryImages[activeImage].label} className="w-full h-full border-none bg-esbelta-lavender/50" data-id="element-121" />}
          </div>
          <div className="grid grid-cols-6 gap-2" data-id="element-122">
            {galleryImages.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} aria-label={`Ver ${img.label}`} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeImage === idx ? 'border-esbelta-violet' : 'border-transparent hover:border-esbelta-violet/30'}`} data-id="element-123">
                {img.src ? <img src={img.src} alt={img.label} className="w-full h-full object-contain bg-white" data-id="element-124" /> : <ImagePlaceholder label={`Img ${idx + 1}`} className="w-full h-full text-[10px] p-1 border-none bg-esbelta-lavender/30" data-id="element-125" />}
              </button>)}
          </div>
        </div>

        {/* Right: Purchase Panel */}
        <div className="w-full lg:w-1/2 flex flex-col" data-id="element-126">
          <div className="flex items-center gap-2 mb-4" data-id="element-127">
            <div className="flex text-esbelta-violet" data-id="element-128">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-129" />)}
            </div>
            <span className="text-sm font-bold text-gray-700" data-id="element-130">4.9</span>
            <span className="text-sm text-gray-500" data-id="element-131">· 3.847 Reseñas</span>
          </div>

          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-esbelta-violet leading-tight mb-6" data-id="element-132">
            ESBELTA® — Tu cuerpo sabe cómo volver a ser tuyo
          </h1>

          <ul className="space-y-3 mb-8" data-id="element-133">
            {['Tu metabolismo lleva años frenado. No por falta de voluntad — por desequilibrio hormonal acumulado.', 'ESBELTA activa el equilibrio metabólico y hormonal desde adentro, usando una Fórmula Ancestral con 8 activos naturales.', 'Diseñada exclusivamente para mujeres maduras que sienten que nada les funciona como antes.', 'Reduce el cortisol que acumula grasa en el abdomen. Baja la inflamación. Devuelve la energía real.', 'Las gomitas que saben bien y transforman de verdad. Resultados visibles en 21 días.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-134">
                <CircleCheckIcon className="w-5 h-5 text-esbelta-violet flex-shrink-0 mt-0.5" data-id="element-135" />
                <span className="text-gray-700 leading-relaxed text-sm md:text-base" data-id="element-136">
                  {item}
                </span>
              </li>)}
          </ul>

          {/* Doctor / Updated Formula badge */}
          <div className="flex items-center gap-4 bg-esbelta-lavender/50 border border-esbelta-violet/15 rounded-xl p-4 mb-8" data-id="element-137">
            <img src="https://cdn.magicpatterns.com/uploads/mw3DGoJVuctR6WNakzDF6h/heylloyd-image-1781540319182.png" alt="Profesional de la salud que respalda ESBELTA®" className="w-20 h-20 rounded-full object-cover flex-shrink-0 bg-white" data-id="element-138" />
            <div data-id="element-139">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-140">
                <BadgeCheckIcon className="w-5 h-5 text-esbelta-violet flex-shrink-0" data-id="element-141" />
                <span className="font-serif font-bold text-gray-900 text-base md:text-lg italic" data-id="element-142">
                  Fórmula Actualizada 2026
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-143">
                Nuestro producto más reciente y actualizado, clínicamente
                probado para darte resultados.
              </p>
            </div>
          </div>

          <div className="mb-6" data-id="element-144">
            <PackSelector selectedPackId={selectedPack} onSelectPack={setSelectedPack} data-id="element-145" />
          </div>

          <div className="flex items-center gap-2 text-red-500 font-medium text-sm mb-4" data-id="element-146">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-147"></div>
            Atención — Quedan 23 unidades disponibles en stock
          </div>

          <button className="w-full bg-esbelta-violet hover:bg-esbelta-dark text-white font-bold text-lg py-4 rounded-xl transition-colors shadow-lg shadow-esbelta-violet/20 mb-6" data-id="element-148">
            QUIERO MI ESBELTA®
          </button>

          {/* Mini FAQ Accordion */}
          <div className="border-t border-gray-200 divide-y divide-gray-200 mb-8" data-id="element-149">
            {faqs.map((faq, idx) => <div key={idx} className="py-3" data-id="element-150">
                <button className="w-full flex items-center justify-between text-left font-bold text-gray-800 hover:text-esbelta-violet transition-colors" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-id="element-151">
                  {faq.q}
                  {openFaq === idx ? <ChevronUpIcon className="w-5 h-5" data-id="element-152" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-153" />}
                </button>
                <AnimatePresence data-id="element-154">
                  {openFaq === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-155">
                      <p className="pt-2 text-sm text-gray-600 leading-relaxed" data-id="element-156">
                        {faq.a}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>

          {/* Floating Review */}
          <div className="bg-esbelta-lavender/50 rounded-xl p-5 border border-esbelta-violet/10 mb-6 relative" data-id="element-157">
            <div className="absolute -top-3 -left-2 text-4xl text-esbelta-violet/20 font-serif" data-id="element-158">
              "
            </div>
            <p className="text-gray-800 font-medium italic mb-3 relative z-10 text-sm md:text-base" data-id="element-159">
              En 3 semanas dejé de sentir esa panza dura de siempre. No cambié
              nada más. Solo agregué las gomitas.
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-160">
              <span className="font-bold text-gray-900" data-id="element-161">
                Patricia G., Buenos Aires
              </span>
              <span data-id="element-162">·</span>
              <span className="text-green-600 font-medium flex items-center gap-1" data-id="element-163">
                <CircleCheckIcon className="w-3 h-3" data-id="element-164" /> Compradora verificada
              </span>
              <span data-id="element-165">·</span>
              <span data-id="element-166">Hace 4 horas</span>
            </div>
          </div>

          <SellerCard data-id="element-167" />
        </div>
      </div>
    </section>;
}